/**
 * Funnel Details Page (MA-UI-023 + MA-UI-038 + MA-UI-040 + MA-UI-041)
 * 
 * Professional editor with compact header mode.
 * - Compact 48px editor header with logo, name, and actions
 * - Tabbed interface: Builder | Analytics | Embed
 * - Full viewport-fit workspace
 */

import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { useFunnel, useSaveDraft, usePublishFunnel, checkVersionConflict, isDraftVersionConflict, conflictCurrentVersion, adoptFunnelDraftVersion, forgetFunnelDraftVersion, isDraftSaveCommitted, isDraftSaveUncertain, committedDraftVersion, reconcileDraftSave, useUndo, useRedo, fetchUndoAvailability } from "@/hooks/useFunnels";
import { adoptionDecision, divergenceMessage, ADOPT, CONFLICT } from "./draftVersionAdoption";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  ArrowLeft,
  Save,
  Globe,
  Loader2,
  Check,
  AlertCircle,
  Sparkles,
  AlertTriangle,
  XCircle,
  Play,
  BarChart3,
  Code2,
  Wrench,
  Settings,
  Undo2,
  Redo2,
  History,
} from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

// Builder components
import VisualFunnelBuilder from "@/components/funnels/builder/VisualFunnelBuilder";
import { normalizeFunnelNodes } from "@/components/funnels/builder/ResponseStepComponents";
import SaveAsTemplateModal from "@/components/funnels/SaveAsTemplateModal";
import ReviewProgressIndicator from "@/components/funnels/ReviewProgressIndicator";
import FunnelAnalyticsTab from "@/components/funnels/FunnelAnalyticsTab";
import FunnelEmbedTab from "@/components/funnels/FunnelEmbedTab";
import FunnelSettingsTab from "@/components/funnels/FunnelSettingsTab";
import { MutationTimeline } from "@/components/funnels/MutationTimeline";
import { FunnelAssistPanel } from "@/components/funnels/FunnelAssistPanel";
import { FunnelHealthCard } from "@/components/funnels/FunnelHealthCard";
import { api } from "@/lib/api";
import { resolveTheme, themeToFunnelAppearance, PRESETS } from "@/lib/themeEngine";
import { convertThemeToLpTokens } from "@/lib/themeAdapters";
import { cleanupStaleOverrides } from "@/lib/appearanceOverrides";  // MA-305: Stale override cleanup
import { toast } from "@/hooks/use-toast";
import { useEditorMode } from "@/contexts/PageTitleContext";
import { LOGO_COLLAPSED_DATA } from "@/assets/logoInline";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { rebaseDecision, rebaseDivergenceMessage, REBASE, DIVERGED } from "./draftRebaseGuard";

// =============================================================================
// Compact Editor Header Component (MA-UI-054: Integrated into OS menu bar)
// MA-UI-105: Single action bar row with back, title, tabs, and actions
// =============================================================================

// Page Action Bar - Back + Title + Tabs (centered) + Actions
function PageActionBar({
  documentName,
  onNameChange,
  isRenaming,
  onRenameSave,
  onBack,
  activeTab,
  onTabChange,
  saveStatus,
  saveIndicator,
  onSave,
  isSaving,
  hasUnsavedChanges,
  onTest,
  onPublish,
  isPublishing,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  isUndoing,
  isRedoing,
  isAIGenerated,
  reviewStats,
  onNavigateToStep,
}) {
  return (
    <div 
      className="h-12 flex items-center justify-between px-4 mx-3 mt-3 flex-shrink-0 rounded-xl border border-white/[0.08] bg-white/[0.04] backdrop-blur-xl"
      data-testid="page-action-bar"
    >
      {/* Left: Back + Document Name */}
      <div className="flex items-center gap-2 flex-1 min-w-0">
        {/* Back button */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 flex-shrink-0"
                onClick={onBack}
                data-testid="back-to-funnels-btn"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Back to Funnels</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Document Name */}
        <div className="flex items-center gap-1.5 min-w-0 max-w-[240px]">
          <Input
            value={documentName}
            onChange={(e) => onNameChange(e.target.value)}
            disabled={isRenaming}
            className="h-7 bg-transparent text-sm text-slate-100 border-transparent hover:border-white/20 focus:border-indigo-400 min-w-0 px-2"
            placeholder="Funnel name"
            onBlur={onRenameSave}
            onKeyDown={(e) => e.key === 'Enter' && onRenameSave()}
            data-testid="funnel-title-input"
          />
          {isRenaming && <Loader2 className="h-3 w-3 animate-spin text-slate-400" />}
        </div>
      </div>
      
      {/* Center: Tab Selector (MA-UI-105: Admin-style tabs) */}
      <div className="flex items-center gap-3">
        <div className="inline-flex h-8 items-center gap-0.5 bg-indigo-500/15 rounded-lg p-0.5 ring-1 ring-indigo-400/20">
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "h-7 px-3 text-xs rounded-md transition-all",
              activeTab === 'builder' 
                ? 'bg-indigo-500/30 text-white shadow-sm' 
                : 'text-indigo-200/70 hover:text-white hover:bg-white/5'
            )}
            onClick={() => onTabChange('builder')}
            data-testid="tab-builder"
          >
            <Wrench className="h-3 w-3 mr-1.5" />
            Builder
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "h-7 px-3 text-xs rounded-md transition-all",
              activeTab === 'analytics' 
                ? 'bg-indigo-500/30 text-white shadow-sm' 
                : 'text-indigo-200/70 hover:text-white hover:bg-white/5'
            )}
            onClick={() => onTabChange('analytics')}
            data-testid="tab-analytics"
          >
            <BarChart3 className="h-3 w-3 mr-1.5" />
            Analytics
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "h-7 px-3 text-xs rounded-md transition-all",
              activeTab === 'embed' 
                ? 'bg-indigo-500/30 text-white shadow-sm' 
                : 'text-indigo-200/70 hover:text-white hover:bg-white/5'
            )}
            onClick={() => onTabChange('embed')}
            data-testid="tab-embed"
          >
            <Code2 className="h-3 w-3 mr-1.5" />
            Embed
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "h-7 px-3 text-xs rounded-md transition-all",
              activeTab === 'settings' 
                ? 'bg-indigo-500/30 text-white shadow-sm' 
                : 'text-indigo-200/70 hover:text-white hover:bg-white/5'
            )}
            onClick={() => onTabChange('settings')}
            data-testid="tab-settings"
          >
            <Settings className="h-3 w-3 mr-1.5" />
            Settings
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "h-7 px-3 text-xs rounded-md transition-all",
              activeTab === 'assist' 
                ? 'bg-indigo-500/30 text-white shadow-sm' 
                : 'text-indigo-200/70 hover:text-white hover:bg-white/5'
            )}
            onClick={() => onTabChange('assist')}
            data-testid="tab-assist"
          >
            <Sparkles className="h-3 w-3 mr-1.5" />
            Assist
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "h-7 px-3 text-xs rounded-md transition-all",
              activeTab === 'history' 
                ? 'bg-indigo-500/30 text-white shadow-sm' 
                : 'text-indigo-200/70 hover:text-white hover:bg-white/5'
            )}
            onClick={() => onTabChange('history')}
            data-testid="tab-history"
          >
            <History className="h-3 w-3 mr-1.5" />
            History
          </Button>
        </div>
        
        {/* MA-UI-107: Review Progress moved to Preview panel header */}
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-2 flex-1 justify-end">
        {/* MA-ARCH-077: Undo/Redo buttons */}
        <div className="flex items-center gap-0.5 mr-1">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-slate-400 hover:text-white disabled:opacity-30"
                  onClick={onUndo}
                  disabled={!canUndo || isUndoing}
                  data-testid="undo-btn"
                >
                  {isUndoing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Undo2 className="h-3.5 w-3.5" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">
                Undo (Ctrl+Z)
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-slate-400 hover:text-white disabled:opacity-30"
                  onClick={onRedo}
                  disabled={!canRedo || isRedoing}
                  data-testid="redo-btn"
                >
                  {isRedoing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Redo2 className="h-3.5 w-3.5" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">
                Redo (Ctrl+Y)
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        
        {/* Save Status -- P4-VFB-UX1 item C: ONE chip for both persistence
            classes. Automatic mutations (node/response edits, Undo, Redo) and
            the explicit draft Save share this surface, resolved by a strict
            priority so a global "Saved" can never show while anything relevant
            is still pending. */}
        {saveIndicator && (
          <div
            className="flex items-center gap-1 text-xs text-slate-400 mr-1"
            data-testid="save-indicator"
            data-save-state={saveIndicator.state}
          >
            {saveIndicator.icon === "spinner" && <Loader2 className="h-3 w-3 animate-spin" />}
            {saveIndicator.icon === "check" && <Check className="h-3 w-3 text-green-400" />}
            {saveIndicator.icon === "error" && <AlertCircle className="h-3 w-3 text-red-400" />}
            {saveIndicator.icon === "warning" && <AlertTriangle className="h-3 w-3 text-amber-400" />}
            <span className={saveIndicator.className}>{saveIndicator.text}</span>
          </div>
        )}

        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs border-white/10"
          onClick={onSave}
          disabled={isSaving || !hasUnsavedChanges || saveStatus === "conflict"}
          data-testid="save-draft-btn"
        >
          {isSaving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}
          Save
        </Button>

        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs border-white/10"
          onClick={onTest}
          data-testid="test-funnel-btn"
        >
          <Play className="h-3 w-3 mr-1" />
          Test
        </Button>

        {/* MA-UI-119: Publish Button - PRIMARY GLOBAL ACTION */}
        {/* Premium surface styling aligned with New Step, but higher visual priority */}
        <Button
          size="sm"
          className={cn(
            // Slightly larger than secondary buttons - higher authority
            "h-8 px-4 text-xs font-medium",
            // Muted indigo gradient - same family as New Step but slightly stronger
            "bg-gradient-to-b from-indigo-500 to-indigo-600",
            // Soft outer glow - slightly more pronounced than New Step
            "shadow-md shadow-indigo-500/25",
            "text-white border-0",
            "rounded-md",
            // Hover: slight lift + brightness
            "hover:from-indigo-400 hover:to-indigo-500",
            "hover:shadow-lg hover:shadow-indigo-500/30",
            "hover:-translate-y-px",
            // Active: slight press
            "active:translate-y-0 active:shadow-sm",
            // Smooth transitions
            "transition-all duration-150 ease-out"
          )}
          onClick={onPublish}
          disabled={isPublishing}
          data-testid="publish-btn"
        >
          {isPublishing ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Globe className="h-3.5 w-3.5 mr-1.5" />}
          Publish
        </Button>
      </div>
    </div>
  );
}

// Legacy wrapper for backward compatibility
function CompactEditorHeader({
  documentName,
  onNameChange,
  isRenaming,
  onRenameSave,
  isPublished,
  isAIGenerated,
  saveStatus,
  saveIndicator,
  onSave,
  isSaving,
  hasUnsavedChanges,
  onTest,
  onPublish,
  isPublishing,
  onBack,
  activeTab,
  onTabChange,
  reviewStats,
  onNavigateToStep,
  baseVersion, // MA-BE-071: Version tracking
  // MA-ARCH-077: Undo/Redo
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  isUndoing,
  isRedoing,
}) {
  return (
    <PageActionBar
      documentName={documentName}
      onNameChange={onNameChange}
      isRenaming={isRenaming}
      onRenameSave={onRenameSave}
      onBack={onBack}
      activeTab={activeTab}
      onTabChange={onTabChange}
      saveStatus={saveStatus}
      saveIndicator={saveIndicator}
      onSave={onSave}
      isSaving={isSaving}
      hasUnsavedChanges={hasUnsavedChanges}
      onTest={onTest}
      onPublish={onPublish}
      isPublishing={isPublishing}
      onUndo={onUndo}
      onRedo={onRedo}
      canUndo={canUndo}
      canRedo={canRedo}
      isUndoing={isUndoing}
      isRedoing={isRedoing}
      isAIGenerated={isAIGenerated}
      reviewStats={reviewStats}
      onNavigateToStep={onNavigateToStep}
    />
  );
}

// =============================================================================
// Main Page Component
// =============================================================================
export default function FunnelDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient(); // MA-229: For header migration
  
  // Enable editor mode (hides large header)
  useEditorMode(true);
  
  // Tab state from URL
  const activeTab = searchParams.get("tab") || "builder";
  const setActiveTab = (tab) => {
    setSearchParams({ tab }, { replace: true });
  };
  
  // Data fetching
  const { data: funnel, isLoading, error, refetch } = useFunnel(id);
  const saveDraft = useSaveDraft();
  const publishFunnel = usePublishFunnel();
  
  // Local state
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [localDraft, setLocalDraft] = useState(null);
  const [saveStatus, setSaveStatus] = useState("idle");
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [saveAsTemplateOpen, setSaveAsTemplateOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // MA-BE-071: Version tracking for mutation API
  const [baseVersion, setBaseVersion] = useState(0);
  const [versionConflict, setVersionConflict] = useState(null);
  
  // MA-ARCH-077: Undo/Redo hooks
  const undo = useUndo();
  const redo = useRedo();
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  // P4-FIX-795: ONE place that answers "may the operator undo or redo?", and
  // it asks the server rather than guessing from the version number. Called
  // on load and after every operation that can change availability.
  const refreshUndoAvailability = useCallback(async () => {
    if (!id) return;
    const { canUndo: undoable, canRedo: redoable } = await fetchUndoAvailability(id);
    setCanUndo(undoable);
    setCanRedo(redoable);
  }, [id]);
  // P4-VFB-UX1 item B/C: automatic-save state, reported by VisualFunnelBuilder
  // and by the page's own Undo/Redo (mutations too, but they live here).
  const [autoPending, setAutoPending] = useState(false);
  const [autoFailure, setAutoFailure] = useState(null);   //: "conflict" | "error" | null
  const [autoSaved, setAutoSaved] = useState(false);
  
  // Funnel name editing state
  const [funnelName, setFunnelName] = useState("");
  const [isRenamingFunnel, setIsRenamingFunnel] = useState(false);
  
  // Appearance settings state
  const [appearance, setAppearance] = useState(null);
  
  // Funnel settings state (MA-UI-059: Compliance, Runtime, Booking, Integrations)
  const [funnelSettings, setFunnelSettings] = useState({});
  
  // Review stats for AI-generated funnels
  const [reviewStats, setReviewStats] = useState(null);
  const [publishValidation, setPublishValidation] = useState(null);
  //: P4-FUNNEL-500-E: for an AI-generated funnel the dialog's action is blocked
  //: both when validation DENIES eligibility and when no verdict was obtained.
  //: Non-AI funnels never validate, so both stay false and their behaviour is
  //: unchanged.
  const [validatingPublish, setValidatingPublish] = useState(false);
  
  // MA-209: Theme loading timeout to prevent infinite hang
  const [themeTimeout, setThemeTimeout] = useState(false);
  
  // Initialize funnel name from fetched data
  useEffect(() => {
    if (funnel?.name && !funnelName) {
      setFunnelName(funnel.name);
    }
  }, [funnel?.name, funnelName]);

  // MA-ARCH-173: Profit Path + Version context for builder header
  const profitPathId = funnel?.profit_path_id;
  const { data: ppContext } = useQuery({
    queryKey: ["pp-ctx", profitPathId],
    queryFn: async () => {
      const [ppRes, vRes] = await Promise.all([
        api.get(`/profit-paths/${profitPathId}`),
        api.get(`/funnel-versions/profit-path/${profitPathId}/versions`),
      ]);
      const versions = vRes.data;
      return {
        pp: ppRes.data,
        activeVersion: versions?.versions?.find(v => v.is_active),
        draftVersion: versions?.versions?.find(v => v.status === "draft"),
      };
    },
    enabled: !!profitPathId,
    staleTime: 30000,
  });
  
  // MA-184: Extract PP Custom Theme (Priority 1 for theme resolution)
  const ppCustomTheme = useMemo(() => {
    if (!ppContext?.pp) return null;
    const foundation = ppContext.pp.builder_data?.foundation;
    const stylingMode = foundation?.theme_config?.styling_mode;
    if (stylingMode === 'custom') {
      const theme = ppContext.pp.builder_data?.theme;
      console.log("[MA-184] PP Custom Theme detected:", theme?.name, "Tokens:", !!theme?.tokens);
      return theme;
    }
    return null;
  }, [ppContext]);
  
  // MA-304: Convert PP theme to lpTokens for renderer (with finalTheme priority)
  const lpTokens = useMemo(() => {
    // MA-304: Priority 1 - Use finalTheme.computed.lpTokens
    if (funnel?.builder_data?.finalTheme?.computed?.lpTokens) {
      console.log("[MA-304][FunnelDetails] Using finalTheme.computed.lpTokens");
      return funnel.builder_data.finalTheme.computed.lpTokens;
    }

    // Fallback: Old PP theme resolution
    if (!ppCustomTheme?.tokens) return null;
    const tokens = convertThemeToLpTokens(ppCustomTheme);
    console.log("[MA-184] Converted lpTokens:", tokens);
    return tokens;
  }, [funnel, ppCustomTheme]);
  
  // Initialize appearance from fetched data
  // MA-THEME-PHASE4: Resolve appearance from brand theme for inherit_brand funnels
  const { data: brandConfig } = useQuery({
    queryKey: ['brand-config'],
    queryFn: () => api.get('/brand-config').then(r => r.data),
    staleTime: 60000,
  });

  useEffect(() => {
    if (!funnel) return;
    
    console.log('[MA-305-TRACE] FunnelDetailsPage - Hydration START:', {
      funnel_id: funnel.id,
      funnel_appearance: funnel.appearance,
      funnel_appearance_overrides: funnel.appearance_overrides,
      headerBg: funnel.appearance?.headerBackgroundColor,
      responseBg: funnel.appearance?.responseAreaColor,
      threadBg: funnel.appearance?.threadColor,
    });
    
    // MA-305: Cleanup stale override intent from broken cycles
    const cleanedOverrides = cleanupStaleOverrides(
      funnel.appearance_overrides,
      funnel.appearance
    );
    
    // If overrides were cleaned, update the funnel reference
    if (cleanedOverrides.length !== (funnel.appearance_overrides || []).length) {
      funnel.appearance_overrides = cleanedOverrides;
      console.log('[MA-305-CLEANUP] Applied cleaned overrides to funnel hydration');
    }
    
    // MA-305: CRITICAL - Remove nested appearance.appearance_overrides
    // Top-level appearance_overrides is authoritative
    // Nested field causes conflicts and must be removed
    if (funnel.appearance && 'appearance_overrides' in funnel.appearance) {
      console.log('[MA-305-CLEANUP] Removing nested appearance.appearance_overrides (using top-level)');
      delete funnel.appearance.appearance_overrides;
    }
    
    // P4-FUNNEL-DRAFT-05: the saved custom funnel icon, carried across every
    // theme-resolved seed below.
    //
    // Each branch REBUILDS appearance from a theme and copies only an explicit
    // list of COLOUR keys, which is correct -- the theme owns the colours and
    // surfaceMode. But `customIconUrl` is not a colour: it is the URL
    // `POST /api/uploads/image` returns, persisted on the funnel by
    // P4-FUNNEL-DRAFT-03 and read back by both consumers of this state
    // (`FunnelPropertiesPanel`'s icon editor and `FunnelCanvasPreview`'s
    // header image). Rebuilding from the theme dropped it, so an uploaded icon
    // vanished from the builder on reload in every theme-resolved mode. Only
    // PRIORITY 3 (custom/legacy), which spreads the saved appearance whole,
    // was unaffected.
    //
    // Spread as ONE named key, never as `...funnel.appearance`: a blanket
    // spread would let the saved colours beat the theme's. Only a non-empty
    // string is carried, matching the backend's own PRESERVED_OPTIONAL_FIELDS
    // rule, so a malformed stored value is ignored rather than rehydrated.
    const savedCustomIcon =
      typeof funnel?.appearance?.customIconUrl === 'string' && funnel.appearance.customIconUrl
        ? { customIconUrl: funnel.appearance.customIconUrl }
        : null;

    // MA-304: PRIORITY 0 - finalTheme from Profit Path (highest priority)
    if (funnel?.builder_data?.finalTheme?.computed?.funnelAppearance) {
      const finalTheme = funnel.builder_data.finalTheme;

      console.log("[MA-304][FunnelDetails] Using finalTheme.computed.funnelAppearance", {
        theme_id: finalTheme.theme_id,
        source: finalTheme.source,
        accent: finalTheme.computed.funnelAppearance.accent,
      });

      const topLevelHeader = funnel.header || {};
      const iconKey =
        topLevelHeader.icon_type === 'default'
          ? 'shield'
          : (topLevelHeader.icon_type || 'shield');

      setAppearance({
        ...finalTheme.computed.funnelAppearance,
        ...(savedCustomIcon || {}),   // P4-FUNNEL-DRAFT-05
        title: topLevelHeader.title || '',
        selectedIcon: iconKey,
        appearance_overrides: funnel.appearance_overrides || [],  // MA-305: Preserve overrides
      });

      return;
    }
    
    // MA-269: REMOVED MA-219 suppression guard
    // Appearance is ALWAYS hydrated from backend (canonical source per MA-267)
    // lpTokens are used as seed/defaults only, NOT as overrides
    
    // MA-184: PRIORITY 1 - PP Custom Theme (highest priority)
    if (ppCustomTheme && lpTokens) {
      console.log("[MA-184] Using PP Custom Theme:", ppCustomTheme.name);
      console.log("[MA-218 DEBUG] PP Theme values:", {
        cardBg: lpTokens.cardBg,
        bgPage: lpTokens.bgPage,
        accent: lpTokens.accent,
        surfaceMode: lpTokens.surfaceMode,
        textPrimary: lpTokens.textPrimary,
      });
      
      // MA-272: Merge canonical appearance with lpTokens defaults
      // Precedence: funnel.appearance (canonical) > lpTokens (seed)
      // Only defined/valid canonical values override lpTokens
      const canonicalAppearance = funnel.appearance || {};
      
      // Derive lpTokens-based defaults (seed values)
      const lpTokensDefaults = {
        containerColor: lpTokens.cardBg,
        threadColor: lpTokens.bgPage,
        accent: lpTokens.accent,
        userColor: lpTokens.accent,
        userTextColor: lpTokens.textOnAccent,
        botTextColor: lpTokens.textPrimary,
        iconBackgroundColor: lpTokens.accent,
        iconColor: lpTokens.textOnAccent,
      };
      
      // Merge: lpTokens defaults + canonical overrides (only when defined)
      // null/undefined canonical values do NOT override lpTokens defaults
      const topLevelHeader = funnel.header || {};
      const iconKey = topLevelHeader.icon_type === 'default' ? 'shield' : (topLevelHeader.icon_type || 'shield');
      
      const mergedAppearance = {
        ...lpTokensDefaults,                                                    // Seed defaults
        ...(canonicalAppearance.containerColor && { containerColor: canonicalAppearance.containerColor }),
        ...(canonicalAppearance.threadColor && { threadColor: canonicalAppearance.threadColor }),
        ...(canonicalAppearance.accent && { accent: canonicalAppearance.accent }),
        ...(canonicalAppearance.userColor && { userColor: canonicalAppearance.userColor }),
        ...(canonicalAppearance.userTextColor && { userTextColor: canonicalAppearance.userTextColor }),
        ...(canonicalAppearance.botTextColor && { botTextColor: canonicalAppearance.botTextColor }),
        ...(canonicalAppearance.iconBackgroundColor && { iconBackgroundColor: canonicalAppearance.iconBackgroundColor }),
        ...(canonicalAppearance.iconColor && { iconColor: canonicalAppearance.iconColor }),
        ...(canonicalAppearance.headerBackgroundColor && { headerBackgroundColor: canonicalAppearance.headerBackgroundColor }),
        // P4-FUNNEL-APPEARANCE-02: carry a SAVED response-area colour.
        // Conditional like its siblings, so it takes precedence over the
        // inherited brand value when the funnel has one and adds nothing when
        // it does not -- the frontend's `themeToFunnelAppearance` never emitted
        // `responseAreaColor`, and MA-305 Priority 2 keeps supplying #f1f5f9
        // for funnels that never set it. Without this the backend repair alone
        // is not enough: GET returns the colour, this seed drops it, and
        // `resolveAppearanceValue` Priority 1 returns `undefined` again because
        // "responseAreaColor" is still in `appearance_overrides`.
        ...(canonicalAppearance.responseAreaColor && { responseAreaColor: canonicalAppearance.responseAreaColor }),
        ...(canonicalAppearance.surfaceMode && { surfaceMode: canonicalAppearance.surfaceMode }),
        ...(savedCustomIcon || {}),   // P4-FUNNEL-DRAFT-05
        title: topLevelHeader.title || '',
        selectedIcon: iconKey,
        appearance_overrides: funnel.appearance_overrides || [],  // MA-305: Preserve overrides
      };
      
      console.log("[MA-272] Merged appearance (canonical > lpTokens):", {
        canonical_containerColor: canonicalAppearance.containerColor,
        lpTokens_cardBg: lpTokens.cardBg,
        final_containerColor: mergedAppearance.containerColor,
        source: mergedAppearance.containerColor === canonicalAppearance.containerColor ? 'canonical' : 'lpTokens',
      });
      
      setAppearance(mergedAppearance);
      return; // PP theme applied, stop here
    }
    
    // MA-184: PRIORITY 2 - Brand Theme (inherit_brand)
    const stylingMode = funnel.styling_mode || 'legacy';
    if (stylingMode === 'inherit_brand' && brandConfig) {
      console.log("[MA-184] Using Brand Theme (inherit_brand)");
      const preset = brandConfig.default_preset || 'professional';
      const pd = PRESETS[preset] || PRESETS.professional;
      const theme = resolveTheme({
        preset,
        anchor_color: brandConfig.default_anchor_color || brandConfig.brand_primary || brandConfig.accent,
        mode: brandConfig.mode_override || pd.mode,
      });
      
      // MA-272: Merge canonical appearance with brand theme defaults
      // Precedence: funnel.appearance (canonical) > brand theme (seed)
      const canonicalAppearance = funnel.appearance || {};
      const brandThemeDefaults = themeToFunnelAppearance(theme);
      
      const topLevelHeader = funnel.header || {};
      const iconKey = topLevelHeader.icon_type === 'default' ? 'shield' : (topLevelHeader.icon_type || 'shield');
      
      const mergedAppearance = {
        ...brandThemeDefaults,                                                  // Seed defaults
        ...(canonicalAppearance.containerColor && { containerColor: canonicalAppearance.containerColor }),
        ...(canonicalAppearance.threadColor && { threadColor: canonicalAppearance.threadColor }),
        ...(canonicalAppearance.accent && { accent: canonicalAppearance.accent }),
        ...(canonicalAppearance.userColor && { userColor: canonicalAppearance.userColor }),
        ...(canonicalAppearance.userTextColor && { userTextColor: canonicalAppearance.userTextColor }),
        ...(canonicalAppearance.botTextColor && { botTextColor: canonicalAppearance.botTextColor }),
        ...(canonicalAppearance.headerBackgroundColor && { headerBackgroundColor: canonicalAppearance.headerBackgroundColor }),
        ...(canonicalAppearance.iconBackgroundColor && { iconBackgroundColor: canonicalAppearance.iconBackgroundColor }),
        ...(canonicalAppearance.iconColor && { iconColor: canonicalAppearance.iconColor }),
        // P4-FUNNEL-APPEARANCE-02: carry a SAVED response-area colour.
        // Conditional like its siblings, so it takes precedence over the
        // inherited brand value when the funnel has one and adds nothing when
        // it does not -- the frontend's `themeToFunnelAppearance` never emitted
        // `responseAreaColor`, and MA-305 Priority 2 keeps supplying #f1f5f9
        // for funnels that never set it. Without this the backend repair alone
        // is not enough: GET returns the colour, this seed drops it, and
        // `resolveAppearanceValue` Priority 1 returns `undefined` again because
        // "responseAreaColor" is still in `appearance_overrides`.
        ...(canonicalAppearance.responseAreaColor && { responseAreaColor: canonicalAppearance.responseAreaColor }),
        ...(savedCustomIcon || {}),   // P4-FUNNEL-DRAFT-05
        title: topLevelHeader.title || '',
        selectedIcon: iconKey,
        appearance_overrides: funnel.appearance_overrides || [],  // MA-305: Preserve overrides
      };
      
      setAppearance(mergedAppearance);
      return;
    }
    
    // MA-184: PRIORITY 3 - Funnel Custom Appearance
    if (stylingMode !== 'inherit_brand' && funnel.appearance) {
      console.log("[MA-184] Using Funnel Custom Appearance");
      
      setAppearance(prev => {
        const newAppearance = {
          ...(prev || funnel.appearance),
          appearance_overrides: funnel.appearance_overrides || [],  // MA-305: Preserve overrides
        };
        return newAppearance;
      });
    }
  }, [funnel?.id, funnel?.styling_mode, ppCustomTheme, lpTokens, brandConfig]);
  
  // MA-209: Theme loading timeout to prevent infinite hang
  // If PP theme is needed but not ready after 3 seconds, proceed with fallback
  useEffect(() => {
    const needsTheme = !!funnel?.profit_path_id;
    const themeReady = ppCustomTheme && lpTokens;
    
    if (needsTheme && !themeReady && !themeTimeout) {
      console.log("[MA-209] Waiting for PP theme... (3s timeout)");
      const timer = setTimeout(() => {
        console.log("[MA-209] Theme timeout reached - proceeding with fallback");
        setThemeTimeout(true);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
    
    // Reset timeout if theme becomes ready
    if (themeReady && themeTimeout) {
      setThemeTimeout(false);
    }
  }, [funnel?.profit_path_id, ppCustomTheme, lpTokens, themeTimeout]);
  
  // Initialize funnel settings from fetched data (MA-UI-059)
  useEffect(() => {
    if (funnel?.settings && Object.keys(funnelSettings).length === 0) {
      setFunnelSettings(funnel.settings);
    }
  }, [funnel?.settings, funnelSettings]);
  
  // Initialize local draft from fetched data
  useEffect(() => {
    if (funnel?.draft && !localDraft) {
      // MA-241: OVERWRITE stale draft.header with canonical funnel.header
      // draft.header is DEPRECATED and must not be used for rendering
      const cleanedDraft = {
        ...funnel.draft,
        header: funnel.header,  // ← Always use canonical header
      };
      
      console.log('[MA-241] Hydrating localDraft with CANONICAL header:', {
        'draft.header (STALE - IGNORED)': funnel.draft.header,
        'funnel.header (CANONICAL - USED)': funnel.header,
      });
      
      setLocalDraft(cleanedDraft);
      if (funnel.draft.entry_node_id) {
        setSelectedNodeId(funnel.draft.entry_node_id);
      }
    }
  }, [funnel, localDraft]);
  
  // MA-229: Lazy migration — Initialize header field from appearance when null
  // This migrates funnels on-demand as users open them, establishing canonical header
  useEffect(() => {
    if (!funnel || funnel.header !== null) return; // Only process funnels with null header
    
    console.log('[MA-229] Detected funnel with null header — initializing from appearance');
    
    // Derive canonical header from appearance or offer
    const initializedHeader = {
      title: funnel.appearance?.title || funnel.offer?.name || "",
      icon_type: funnel.appearance?.selectedIcon || "shield",
    };
    
    console.log('[MA-229] Initializing header:', initializedHeader);
    
    // Persist to database immediately (silent update)
    api.patch(`/funnels/${funnel.id}`, { header: initializedHeader })
      .then(() => {
        console.log('[MA-229] Header initialized successfully');
        // Refetch funnel to get updated header field
        queryClient.invalidateQueries(['funnel', funnel.id]);
      })
      .catch(error => {
        console.error('[MA-229] Failed to initialize header:', error);
      });
  }, [funnel?.id, funnel?.header]);
  
  // MA-BE-071: Initialize base version from funnel data
  // MA-ARCH-077: Also initialize canUndo based on version (if version > 0, there may be undoable mutations)
  //
  // P4-R2C-PROD-FIX-001 (#794). This used to adopt `funnel.draft_version`
  // unconditionally and clear any displayed conflict. With unsaved edits in
  // `localDraft` that was a silent REBASE: another writer commits v9, a
  // refetch lands, `baseVersion` becomes 9, and the next Save sends
  // `expected_draft_version: 9` carrying stale content -- the compare-and-swap
  // MATCHES and the other writer's work is overwritten. The CAS cannot defend
  // against a version the client was never entitled to claim, so the version
  // and the content it describes now move together or not at all.
  useEffect(() => {
    const decision = rebaseDecision({
      serverVersion: funnel?.draft_version,
      baseVersion,
      hasUnsavedChanges,
    });
    if (decision.action === REBASE) {
      if (decision.version !== baseVersion) {
        setBaseVersion(decision.version);
      }
      setVersionConflict(null); // Reset conflict on fresh load
      // P4-FIX-795: was `setCanUndo(decision.version > 0)`. A version above
      // zero does not mean an undoable mutation exists.
      refreshUndoAvailability();
    } else if (decision.action === DIVERGED) {
      // Deliberately NOT clearing the conflict and NOT touching `localDraft`
      // or `hasUnsavedChanges`: the edits survive and the operator chooses.
      setVersionConflict({
        baseVersion,
        currentVersion: decision.version,
        message: rebaseDivergenceMessage(baseVersion, decision.version),
      });
    }
    // DEPENDENCIES ARE DELIBERATELY UNCHANGED from before this fix: the effect
    // runs only when the SERVER version changes. Adding `baseVersion` here
    // re-ran it after `handleSave` had already adopted the committed version,
    // and it then re-asserted the older server value over the newer local one
    // -- reverting a correct adoption. The existing adoption suites caught
    // that. React rebuilds this closure every render, so when the server
    // version does change the effect still reads the CURRENT dirty state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [funnel?.draft_version]);
  
  // Fetch review stats when funnel is AI-generated
  useEffect(() => {
    const fetchReviewStats = async () => {
      if (funnel?.ai_generated && id) {
        try {
          const response = await api.get(`/ai-builder/funnels/${id}/review-stats`);
          setReviewStats(response.data);
        } catch (error) {
          console.error("Failed to fetch review stats:", error);
        }
      }
    };
    fetchReviewStats();
  }, [funnel?.ai_generated, id, localDraft]);
  
  // Get nodes from local draft
  // MA-FE-079: Normalize legacy node types (message, question) -> response on READ
  const nodes = useMemo(() => {
    const rawNodes = localDraft?.nodes || [];
    return normalizeFunnelNodes(rawNodes);
  }, [localDraft]);
  
  // Get selected node
  const selectedNode = useMemo(() => {
    if (!selectedNodeId || !nodes.length) return null;
    return nodes.find(n => n.id === selectedNodeId) || null;
  }, [selectedNodeId, nodes]);
  
  // Check if funnel is AI-generated
  const isAIGenerated = funnel?.ai_generated || false;
  //: P4-FUNNEL-500-E. `publishValidation` is null in two distinct situations:
  //: before any validation has run, and after a validation REQUEST failed
  //: (`validateForPublish` catches and returns null). For an AI-generated
  //: funnel `handlePublishClick` always validates before opening the dialog, so
  //: a null inside the dialog means "no verdict obtained" -- which must block,
  //: not permit. Non-AI funnels never validate, so both of these stay false and
  //: their publish path is untouched.
  const publishValidationUnavailable = isAIGenerated && !publishValidation;
  const publishBlocked = isAIGenerated && (!publishValidation || !publishValidation.can_publish);
  
  // Calculate local review stats from draft
  const localReviewStats = useMemo(() => {
    if (!localDraft?.nodes) return null;
    
    const allNodes = localDraft.nodes;
    const aiGeneratedNodes = allNodes.filter(n => n.metadata?.ai_generated);
    const reviewedNodes = allNodes.filter(n => n.metadata?.ai_reviewed);
    const consentNodes = allNodes.filter(n => n.node_type === "consent");
    const consentTextEntered = consentNodes.length === 0 || 
      consentNodes.every(n => n.content?.consent_text);
    
    return {
      total_nodes: allNodes.length,
      ai_generated: aiGeneratedNodes.length,
      reviewed: reviewedNodes.length,
      unreviewed: aiGeneratedNodes.length - reviewedNodes.length,
      consent_text_entered: consentTextEntered,
      can_publish: aiGeneratedNodes.length === reviewedNodes.length && consentTextEntered,
      unreviewed_nodes: aiGeneratedNodes
        .filter(n => !n.metadata?.ai_reviewed)
        .map(n => ({
          id: n.id,
          node_type: n.node_type,
          label: n.metadata?.label || n.node_type
        }))
    };
  }, [localDraft]);
  
  // Save draft to server (MA-BE-071: with version conflict detection)
  const handleSave = useCallback(async () => {
    // P4-FIX-797 / D2. DECLARED HERE, OUTSIDE THE `try` BELOW.
    //
    // It used to be `const savePayload = {...}` INSIDE the try block, while the
    // uncertain-outcome branch in the `catch` reads it to reconcile. A `const`
    // declared in a `try` is not in scope in its `catch`, so that branch threw
    // `ReferenceError: savePayload is not defined` and the page never reached
    // its uncertain state at all. Hoisted rather than duplicated so the
    // reconciliation compares the EXACT object that was submitted.
    let savePayload = null;
    if (!localDraft || !id) return;
    
    setSaveStatus("saving");
    setVersionConflict(null);
    
    try {
      // MA-BE-071: Check for version conflict before saving
      const conflictCheck = await checkVersionConflict(id, baseVersion);
      
      if (conflictCheck.hasConflict) {
        setSaveStatus("conflict");
        setVersionConflict({
          baseVersion,
          currentVersion: conflictCheck.currentVersion,
          message: `Another session has modified this funnel (v${baseVersion} → v${conflictCheck.currentVersion}). Please refresh to get the latest changes before saving.`
        });
        return;
      }
      
      console.log('[MA-305-TRACE] FunnelDetailsPage - handleSave BEFORE:', {
        appearance_state: appearance,
        appearance_overrides: appearance?.appearance_overrides,
        headerBg: appearance?.headerBackgroundColor,
        responseBg: appearance?.responseAreaColor,
        threadBg: appearance?.threadColor,
      });
      
      savePayload = {
        id,
        // P4-R2C: REQUIRED. The server commits only if the stored funnel is
        // still at this version, then advances it by one. The pre-flight
        // `checkVersionConflict` above is a separate request and cannot close
        // the window between its read and this write -- and it returns
        // `hasConflict: false` on any transport error -- so this field, not
        // that check, is what makes a concurrent save detectable.
        expected_draft_version: baseVersion,
        entry_node_id: localDraft.entry_node_id,
        nodes: localDraft.nodes,
        header: localDraft.header,
        settings: localDraft.settings,
        admin: localDraft.admin,
        appearance: appearance,
        appearance_overrides: appearance?.appearance_overrides || [],  // MA-305: Explicit field
        funnel_settings: funnelSettings,
      };
      
      console.log('[MA-305-TRACE] FunnelDetailsPage - handleSave PAYLOAD:', {
        appearance: savePayload.appearance,
        appearance_overrides: savePayload.appearance_overrides,
        headerBg: savePayload.appearance?.headerBackgroundColor,
        responseBg: savePayload.appearance?.responseAreaColor,
        threadBg: savePayload.appearance?.threadColor,
      });
      
      const response = await saveDraft.mutateAsync(savePayload);
      
      console.log('[MA-305-TRACE] FunnelDetailsPage - handleSave RESPONSE:', {
        appearance: response?.appearance,
        appearance_overrides: response?.appearance_overrides,
        headerBg: response?.appearance?.headerBackgroundColor,
        responseBg: response?.appearance?.responseAreaColor,
        threadBg: response?.appearance?.threadColor,
      });
      
      // P4-VFB-R3A: adopt the version the SERVER reports, never a guessed one.
      //
      // This used to be `setBaseVersion(prev => prev + 1)`, which invented an
      // advance that `PUT /draft` does not make: `save_draft` writes
      // `version.draft` and `updated_at` and never touches `draft_version`. The
      // client therefore moved to N+1 while the server stayed at N, and the
      // next mutation was correctly rejected with "Version mismatch: expected
      // N+1, current is N" -- the client AHEAD of the server, the mirror image
      // of the queue race P4-VFB-R2A fixed.
      //
      // The comment that stood here claimed "the refetch will update it from
      // server". It cannot: `useSaveDraft.onSuccess` only invalidates the
      // query, the refetch returns the UNCHANGED `draft_version`, and the
      // reconciliation effect above is keyed on `[funnel?.draft_version]` -- so
      // with the value unchanged React skips it and nothing corrects the guess.
      // Correctness must not depend on that effect re-running.
      //
      // `_funnel_to_response` emits `draft_version` from the same top-level
      // field the mutation engine reads, so the response is authoritative.
      // Adopting it rather than deleting the increment outright also keeps this
      // correct under a future guarded Save (R2C), which WILL advance the
      // version: the new value simply arrives in the same field.
      if (Number.isInteger(response?.draft_version)) {
        setBaseVersion(response.draft_version);
        // P4-R2C-002 (§6.2). Keep the SHARED cache in step. The Profit Path
        // writer caches the authoritative version per funnel id; this page
        // never touched that cache, so a save here left it stale and the next
        // Profit Path sync reported a conflict that was really this user's own
        // edit. Nothing was ever overwritten -- the writer re-reads on
        // conflict -- but the operator saw a conflict they had caused
        // themselves and could not act on.
        adoptFunnelDraftVersion(id, response.draft_version);
      }
      setSaveStatus("saved");
      setHasUnsavedChanges(false);
      setTimeout(() => setSaveStatus("idle"), 2000);
    } catch (error) {
      // P4-R2C: a stale-version rejection is a CONFLICT, not a generic
      // failure. `localDraft` and `hasUnsavedChanges` are deliberately left
      // untouched so the edits survive -- the user chooses between retrying
      // against the fresh version and discarding, and nothing is dropped on
      // their behalf.
      if (isDraftVersionConflict(error)) {
        const serverVersion = conflictCurrentVersion(error);
        setSaveStatus("conflict");
        setVersionConflict({
          baseVersion,
          currentVersion: serverVersion,
          message: serverVersion === null
            ? "Another session has modified this funnel. Refresh to get the latest changes before saving."
            : `Another session has modified this funnel (v${baseVersion} → v${serverVersion}). Your changes are still here; refresh to get the latest version before saving.`,
        });
        return;
      }
      // P4-FIX-797. A CONFIRMED COMMIT must never present as "Not saved".
      //
      // `draft_committed: true` means the write is durable and only the
      // response body was unavailable. Measured before this change: four
      // independent malformed-field triggers each advanced the version,
      // persisted the content, and answered a bare 500 -- the page showed
      // "Not saved -- try again", the user retried, and the retry was refused
      // as DRAFT_VERSION_CONFLICT and rendered as "Another session has
      // modified this funnel", blaming a third party for the user's own save.
      if (isDraftSaveCommitted(error)) {
        const committedVersion = committedDraftVersion(error);
        if (committedVersion !== null) {
          setBaseVersion(committedVersion);
          adoptFunnelDraftVersion(id, committedVersion);
        } else {
          forgetFunnelDraftVersion(id);
        }
        //: the edits ARE persisted, so they are no longer unsaved.
        setHasUnsavedChanges(false);
        setSaveStatus("committed-unavailable");
        return;
      }

      // P4-FIX-797. UNCERTAIN: resolved by reading, never by retrying.
      if (isDraftSaveUncertain(error)) {
        //: if the throw happened before the payload was assembled, there is
        //: nothing to attribute -- `reconcileDraftSave` returns
        //: "indeterminate" for a null payload, which is the right answer.
        const verdict = await reconcileDraftSave(id, baseVersion, savePayload);
        if (verdict === "committed") {
          setBaseVersion(baseVersion + 1);
          adoptFunnelDraftVersion(id, baseVersion + 1);
          setHasUnsavedChanges(false);
          setSaveStatus("committed-unavailable");
          return;
        }
        //: Discard the cached version either way: keeping it would make the
        //: next save collide with our own possible commit.
        forgetFunnelDraftVersion(id);
        if (verdict === "not-committed") {
          setSaveStatus("error");
          setTimeout(() => setSaveStatus("idle"), 3000);
          return;
        }
        //: INDETERMINATE. The edits are deliberately KEPT unsaved and no
        //: retry is offered -- the operator reloads and decides.
        setSaveStatus("uncertain");
        return;
      }

      setSaveStatus("error");
      setTimeout(() => setSaveStatus("idle"), 3000);
    }
  }, [localDraft, id, saveDraft, appearance, funnelSettings, baseVersion]);
  
  // MA-BE-071: Handle version conflict refresh
  const handleRefreshAfterConflict = useCallback(async () => {
    setVersionConflict(null);
    setSaveStatus("idle");
    setLocalDraft(null); // Clear local draft to force reload
    await refetch();
    toast({ 
      title: "Funnel refreshed", 
      description: "Loaded the latest version. Your unsaved changes were discarded." 
    });
  }, [refetch]);
  
  // Rename funnel
  const handleRenameFunnel = useCallback(async () => {
    if (!id || !funnelName.trim() || funnelName === funnel?.name) return;
    
    setIsRenamingFunnel(true);
    try {
      await api.patch(`/funnels/${id}`, { name: funnelName.trim() });
      toast({ title: "Funnel renamed" });
      await refetch();
    } catch (error) {
      toast({
        title: "Rename failed",
        description: error?.response?.data?.detail || "Please try again.",
        variant: "destructive"
      });
      if (funnel?.name) setFunnelName(funnel.name);
    } finally {
      setIsRenamingFunnel(false);
    }
  }, [id, funnelName, funnel?.name, refetch]);
  
  // MA-BE-072: Handle successful mutation from builder
  const handleAutoSaveStateChange = useCallback((state, reason) => {
    if (state === "pending") {
      setAutoPending(true);
      setAutoSaved(false);
      return;
    }
    setAutoPending(false);
    if (state === "saved") {
      // P4-VFB-UX1 final correction: a success NEVER clears an unresolved
      // automatic failure -- not a version conflict, and not a transport error.
      //
      // Both leave an edit the server does not have: a rejected mutation wrote
      // nothing (the engine returns REJECTED before any write) and a thrown one
      // never landed, while in both cases the optimistic edit stays on the
      // canvas by design. A later mutation succeeding says nothing about THAT
      // edit, so treating it as recovery showed "Saved" over unpersisted work.
      // Clearing it conservatively leaves exactly two resolutions, both of
      // which re-establish authoritative server state: a genuine Funnel change
      // (the "idle" branch below) or a full editor reload, which remounts the
      // page and resets this state naturally.
      //
      // The acknowledgment is still recorded -- the mutation DID succeed -- but
      // `saveIndicator` checks `autoFailure` first, so an unresolved failure
      // keeps the surface. No automatic retry is introduced.
      setAutoSaved(true);
      return;
    }
    if (state === "failed") {
      setAutoFailure(reason === "conflict" ? "conflict" : "error");
      setAutoSaved(false);
      return;
    }
    //: "idle" -- a different funnel. Clear everything.
    setAutoFailure(null);
    setAutoSaved(false);
  }, []);

  //: Undo/Redo are automatic persistence too, but they are issued here rather
  //: than in the builder, so they join the pending dimension directly.
  const isUndoRedoPending = undo.isPending || redo.isPending;

  // P4-VFB-UX1 item C: the single derived indicator. Strict priority, first
  // match wins:
  //   1 unresolved failure / conflict   2 anything pending
  //   3 genuine unsaved local changes   4 confirmed saved    5 hidden
  //
  // Failure outranks pending so a rejection is not masked by the next edit, and
  // pending outranks saved so no "Saved" appears while work is outstanding --
  // including between queued mutations, because the builder keeps reporting
  // "pending" until its queue drains.
  const saveIndicator = useMemo(() => {
    const FAIL_SYNC = { state: "not-saved-conflict", icon: "warning",
                        className: "text-amber-400", text: "Not saved \u2014 refresh to sync" };
    const FAIL_RETRY = { state: "not-saved-error", icon: "error",
                         className: "text-red-400", text: "Not saved \u2014 try again" };
    if (autoFailure === "conflict") return FAIL_SYNC;
    if (autoFailure === "error") return FAIL_RETRY;
    if (saveStatus === "conflict") {
      return { state: "conflict", icon: "warning", className: "text-amber-400",
               text: "Conflict \u2014 refresh to sync" };
    }
    // P4-FIX-797. Three outcomes, three surfaces. Neither of these two is
    // "Not saved": one IS saved, the other is unknown, and inviting a retry
    // for either is what caused a committed edit to be re-applied.
    if (saveStatus === "committed-unavailable") {
      return { state: "saved-stale", icon: "check", className: "text-green-400",
               text: "Saved \u2014 reload to refresh" };
    }
    if (saveStatus === "uncertain") {
      return { state: "uncertain", icon: "warning", className: "text-amber-400",
               text: "Outcome unknown \u2014 reload to check" };
    }
    if (saveStatus === "error") return FAIL_RETRY;
    if (autoPending || saveStatus === "saving" || isUndoRedoPending) {
      return { state: "saving", icon: "spinner", className: "", text: "Saving\u2026" };
    }
    if (hasUnsavedChanges) {
      return { state: "unsaved", icon: null, className: "", text: "Unsaved changes" };
    }
    if (autoSaved || saveStatus === "saved") {
      return { state: "saved", icon: "check", className: "text-green-400", text: "Saved" };
    }
    return null;
  }, [autoFailure, autoPending, autoSaved, saveStatus, hasUnsavedChanges, isUndoRedoPending]);

  //: the confirmed Saved state fades, matching the existing draft chip.
  useEffect(() => {
    if (!autoSaved) return undefined;
    const t = setTimeout(() => setAutoSaved(false), 2000);
    return () => clearTimeout(t);
  }, [autoSaved]);

  const handleMutationSuccess = useCallback(({ newVersion, mutationId, idMappings }) => {
    // Update baseVersion immediately for subsequent mutations
    setBaseVersion(newVersion);
    // Clear any stale version conflict
    setVersionConflict(null);
    // P4-FIX-795: was `setCanUndo(newVersion > 0)` plus an optimistic
    // `setCanRedo(false)`. A real mutation DOES write history, so Undo is
    // normally available here -- but the server is still the one that says so,
    // and it also settles whether the new mutation cleared the redo entry.
    refreshUndoAvailability();
    // Note: We don't set hasUnsavedChanges to true because mutation API
    // already persisted the change - it's saved on the backend
    console.log(`[Mutation] Applied ${mutationId}, version now ${newVersion}`, idMappings);
  }, [refreshUndoAvailability]);
  
  // MA-ARCH-077: Undo handler
  const handleUndo = useCallback(async () => {
    if (!id || undo.isPending || baseVersion === 0) return;
    
    try {
      const result = await undo.mutateAsync({ funnelId: id });
      if (result.status === "applied") {
        setBaseVersion(result.new_version);
        // P4-FIX-795: was an optimistic `setCanRedo(true)` plus
        // `setCanUndo(result.new_version > 0)`. `undo()` applies inverse
        // operations through the normal engine, so it ADVANCES the version and
        // writes an `_is_undo` history entry -- whether a further Undo remains
        // depends on history, not on the version being above zero.
        refreshUndoAvailability();
        // Refresh local state
        setLocalDraft(null);
        await refetch();
      } else if (result.error) {
        if (result.error.includes("Nothing to undo")) {
          setCanUndo(false);
        }
      }
    } catch (error) {
      console.error("Undo failed:", error);
    }
  }, [id, undo, baseVersion, refetch, refreshUndoAvailability]);
  
  // MA-ARCH-077: Redo handler
  const handleRedo = useCallback(async () => {
    if (!id || redo.isPending || !canRedo) return;
    
    try {
      const result = await redo.mutateAsync({ funnelId: id });
      if (result.status === "applied") {
        setBaseVersion(result.new_version);
        // P4-FIX-795: was an unconditional `setCanUndo(true)`.
        refreshUndoAvailability();
        // Refresh local state
        setLocalDraft(null);
        await refetch();
      } else if (result.error) {
        if (result.error.includes("Nothing to redo")) {
          setCanRedo(false);
        }
      }
    } catch (error) {
      console.error("Redo failed:", error);
    }
  }, [id, redo, canRedo, refetch, refreshUndoAvailability]);
  
  // MA-ARCH-077: Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check if we're in an input field
      const isInputActive = ['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName);
      if (isInputActive) return;
      
      // Ctrl+Z or Cmd+Z for undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      }
      // Ctrl+Y or Cmd+Shift+Z for redo
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        handleRedo();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, handleRedo]);
  
  // Handle test button
  const handleTestFunnel = useCallback(() => {
    if (!funnel?.slug) return;
    const testUrl = `/funnel?funnel=${funnel.slug}&test_mode=true`;
    window.open(testUrl, '_blank');
  }, [funnel?.slug]);
  
  // Validate before publish
  const validateForPublish = useCallback(async () => {
    if (!id) return null;
    
    setValidatingPublish(true);
    try {
      const response = await api.get(`/ai-builder/funnels/${id}/validate-publish`);
      setPublishValidation(response.data);
      return response.data;
    } catch (error) {
      console.error("Validation failed:", error);
      return null;
    } finally {
      setValidatingPublish(false);
    }
  }, [id]);
  
  // Publish funnel
  const handlePublish = useCallback(async () => {
    if (hasUnsavedChanges) {
      await handleSave();
    }
    
    if (isAIGenerated) {
      const validation = await validateForPublish();
      // P4-FUNNEL-500-E: `validateForPublish` returns null when the request
      // itself failed (HTTP error, network rejection, malformed body). The old
      // guard was `validation && !validation.can_publish`, so a null fell
      // THROUGH to the publish mutation -- a transport error was silently
      // treated as a successful validation. An unobtained verdict is not
      // permission to publish.
      if (!validation) {
        toast({
          title: "Cannot publish yet",
          description: "Publish validation could not be completed. Please try again.",
          variant: "destructive"
        });
        setPublishDialogOpen(false);
        return;
      }
      if (!validation.can_publish) {
        toast({
          title: "Cannot publish yet",
          description: validation.issues[0]?.message || "Please review all steps first",
          variant: "destructive"
        });
        setPublishDialogOpen(false);
        return;
      }
    }
    
    try {
      await publishFunnel.mutateAsync(id);
      setPublishDialogOpen(false);
      await refetch();
      toast({ title: "Funnel published!", description: "Your funnel is now live." });
    } catch (error) {
      // Error handled by mutation
    }
  }, [hasUnsavedChanges, handleSave, isAIGenerated, validateForPublish, publishFunnel, id, refetch]);
  
  // Handle publish button click
  const handlePublishClick = useCallback(async () => {
    if (isAIGenerated) {
      const validation = await validateForPublish();
      setPublishValidation(validation);
    }
    setPublishDialogOpen(true);
  }, [isAIGenerated, validateForPublish]);
  
  // Navigate to step from review indicator
  const handleNavigateToStep = useCallback((nodeId) => {
    setSelectedNodeId(nodeId);
  }, []);
  
  // MA-AI-REVIEW: Mark all unreviewed AI nodes as approved
  const [isMarkingApproved, setIsMarkingApproved] = useState(false);
  
  const handleMarkAllApproved = useCallback(async () => {
    if (!id || !localDraft?.nodes) return;
    
    // Get all unreviewed AI-generated node IDs
    const unreviewedNodeIds = localDraft.nodes
      .filter(n => n.metadata?.ai_generated && !n.metadata?.ai_reviewed)
      .map(n => n.id);
    
    if (unreviewedNodeIds.length === 0) {
      toast({
        title: "All steps reviewed",
        description: "All AI-generated steps have already been reviewed.",
      });
      return;
    }
    
    setIsMarkingApproved(true);
    
    try {
      // Call backend to mark nodes as reviewed
      const approval = await api.post(`/ai-builder/funnels/${id}/mark-reviewed`, {
        node_ids: unreviewedNodeIds
      });
      
      // Update local state
      setLocalDraft(prev => {
        if (!prev) return prev;
        const updatedNodes = prev.nodes.map(node => {
          if (unreviewedNodeIds.includes(node.id)) {
            return {
              ...node,
              metadata: {
                ...node.metadata,
                ai_reviewed: true,
                ai_reviewed_at: new Date().toISOString()
              }
            };
          }
          return node;
        });
        return { ...prev, nodes: updatedNodes };
      });

      // P4-R2C-PROD-FIX-001 (#796). The approval now ADVANCES `draft_version`
      // on the server, so this page's `baseVersion` is stale the instant the
      // POST returns. Adopting it stops the next manual Save from conflicting
      // with THIS user's own approval -- the self-inflicted conflict that
      // R2C-002 already had to remove from the Profit Path version cache.
      //
      // Adopted ONLY when the committed version is exactly our predecessor
      // plus one, i.e. our approval was the single intervening write. A larger
      // jump means another writer also committed, and THAT writer's content is
      // not in `localDraft` -- adopting then would rebase unreconciled edits
      // and let the next Save overwrite them, which is the #794 defect. So the
      // version is left alone and the divergence is surfaced instead, making
      // the next Save conflict rather than clobber.
      const decision = adoptionDecision(baseVersion, approval?.data?.draft_version);
      if (decision.action === ADOPT) {
        setBaseVersion(decision.version);
        adoptFunnelDraftVersion(id, decision.version);
      } else if (decision.action === CONFLICT) {
        setVersionConflict({
          baseVersion,
          currentVersion: decision.version,
          message: divergenceMessage(baseVersion, decision.version),
        });
      }

      // P4-FUNNEL-500-C: refresh the stats in their OWN try.
      //
      // This await used to sit bare inside the approval's try, so a failing
      // `review-stats` jumped straight to the catch below and reported
      // "Approval failed" for an approval the server had already accepted.
      // The mark-reviewed POST above is the only thing whose failure means the
      // approval failed; a stats refresh is a display concern.
      //
      // Nothing is invented on success: the toast below is still reached only
      // after the POST resolves, and a genuinely failed POST still throws past
      // this block into the catch.
      try {
        const response = await api.get(`/ai-builder/funnels/${id}/review-stats`);
        setReviewStats(response.data);
      } catch (statsError) {
        console.error("Approval succeeded; review stats refresh failed:", statsError);
      }
      
      toast({
        title: "Steps approved",
        description: `${unreviewedNodeIds.length} step${unreviewedNodeIds.length > 1 ? 's' : ''} marked as reviewed.`,
      });
    } catch (error) {
      console.error("Failed to mark steps as reviewed:", error);
      toast({
        title: "Approval failed",
        description: "Failed to mark steps as reviewed. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsMarkingApproved(false);
    }
  }, [id, localDraft?.nodes, baseVersion]);
  
  // Determine if approval button should be shown
  // Show when: AI-generated funnel AND there are unreviewed nodes
  const showApprovalButton = useMemo(() => {
    if (!isAIGenerated) return false;
    const stats = reviewStats || localReviewStats;
    if (!stats) return false;
    // Show button if there are AI-generated nodes that haven't been reviewed
    return stats.ai_generated > 0 && stats.unreviewed > 0;
  }, [isAIGenerated, reviewStats, localReviewStats]);
  
  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "s") {
        e.preventDefault();
        handleSave();
      }
    };
    
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleSave]);
  
  // Loading state
  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }
  
  // Error state
  if (error || !funnel) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <AlertCircle className="h-12 w-12 text-destructive" />
        <p className="text-slate-400">Failed to load funnel</p>
        <Button variant="outline" onClick={() => navigate("/app/funnels")}>
          Back to Funnels
        </Button>
      </div>
    );
  }
  
  const displayStats = reviewStats || localReviewStats;
  
  return (
    <div 
      className="flex flex-col h-full overflow-hidden bg-white/[0.02]" 
      data-testid="funnel-details-page"
    >
      {/* MA-UI-105: Page Action Bar - sits on page background, visually separate from OS header */}
      <CompactEditorHeader
        documentName={funnelName}
        onNameChange={setFunnelName}
        isRenaming={isRenamingFunnel}
        onRenameSave={handleRenameFunnel}
        isPublished={!!funnel.published_at}
        isAIGenerated={isAIGenerated}
        saveStatus={saveStatus}
        saveIndicator={saveIndicator}
      saveIndicator={saveIndicator}
        onSave={handleSave}
        isSaving={saveDraft.isPending}
        hasUnsavedChanges={hasUnsavedChanges}
        onTest={handleTestFunnel}
        onPublish={handlePublishClick}
        isPublishing={publishFunnel.isPending || validatingPublish}
        onBack={() => navigate("/app/funnels")}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        reviewStats={displayStats}
        onNavigateToStep={handleNavigateToStep}
        baseVersion={baseVersion}
        onUndo={handleUndo}
        onRedo={handleRedo}
        canUndo={canUndo}
        canRedo={canRedo}
        isUndoing={undo.isPending}
        isRedoing={redo.isPending}
      />
      
      {/* MA-ARCH-173: Profit Path Context Bar */}
      {ppContext?.pp && (
        <div className="flex items-center gap-3 px-6 py-1 bg-indigo-500/[0.03] border-b border-white/[0.05] text-[11px]" data-testid="pp-context-bar">
          <span className="text-slate-500">Strategy:</span>
          <Link to={`/app/profit-paths/${ppContext.pp.id}`} className="text-indigo-300 hover:text-indigo-200 font-medium">
            {ppContext.pp.name}
          </Link>
          {ppContext.pp.message_category && (
            <>
              <span className="text-slate-700">|</span>
              <span className="text-slate-400 capitalize">{ppContext.pp.message_category}</span>
            </>
          )}
          {ppContext.pp.message_variant && (
            <>
              <span className="text-slate-700">|</span>
              <span className="text-purple-300/70">{ppContext.pp.message_variant}</span>
            </>
          )}
          <span className="text-slate-700">|</span>
          {ppContext.activeVersion ? (
            <span className="text-emerald-400/80">
              v{ppContext.activeVersion.version_number}
              {ppContext.activeVersion.version_label && ` — ${ppContext.activeVersion.version_label}`}
            </span>
          ) : (
            <span className="text-amber-400/80">No published version</span>
          )}
          {ppContext.draftVersion && (
            <Badge variant="outline" className="text-[9px] px-1 py-0 text-amber-300/60 border-amber-500/20">
              v{ppContext.draftVersion.version_number} draft
            </Badge>
          )}
        </div>
      )}

      {/* MA-BE-071: Version Conflict Warning Banner */}
      {versionConflict && (
        <Alert 
          variant="warning" 
          className="mx-4 mt-2 border-amber-500/50 bg-amber-500/10"
          data-testid="version-conflict-alert"
        >
          <AlertTriangle className="h-4 w-4 text-amber-400" />
          <AlertDescription className="flex items-center justify-between">
            <span className="text-amber-200">
              {versionConflict.message}
            </span>
            <Button 
              variant="outline" 
              size="sm" 
              className="ml-4 h-7 text-xs border-amber-500/50 text-amber-200 hover:bg-amber-500/20"
              onClick={handleRefreshAfterConflict}
              data-testid="refresh-after-conflict-btn"
            >
              Refresh Funnel
            </Button>
          </AlertDescription>
        </Alert>
      )}
      
      {/* Tab Content - Full Height (MA-UI-054: No duplicate tab bar, tabs in header) */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
        
        {/* Builder Tab Content */}
        <TabsContent value="builder" className="flex-1 overflow-hidden m-0 data-[state=inactive]:hidden flex flex-col">
          {/* MA-FUNNEL-HEALTH-SCORE-013 + MA-FUNNEL-AUTO-FIX-014: Health Card */}
          {funnel?.health && (
            <div className="px-3 pt-2 pb-0 shrink-0">
              <FunnelHealthCard
                health={funnel.health}
                onIssueClick={(issue) => {
                  if (issue.node_id) {
                    const event = new CustomEvent('select-builder-node', { detail: { nodeId: issue.node_id } });
                    window.dispatchEvent(event);
                  }
                }}
                onApplyFix={async (action, payload) => {
                  try {
                    const res = await api.post(`/funnels/${id}/apply-fix`, { action, payload });
                    const data = res.data;
                    if (data.applied && data.funnel) {
                      // Refetch funnel to get updated state (single source of truth)
                      await refetch();
                      // Update local draft with response data
                      if (data.funnel.draft) {
                        setLocalDraft(data.funnel.draft);
                      }
                      setBaseVersion(data.funnel.draft_version || 0);
                      // Scroll to affected node
                      if (data.affected_node_ids?.[0]) {
                        setTimeout(() => {
                          const event = new CustomEvent('select-builder-node', {
                            detail: { nodeId: data.affected_node_ids[0] }
                          });
                          window.dispatchEvent(event);
                        }, 300);
                      }
                    }
                    return data;
                  } catch (err) {
                    return { applied: false, error: err?.response?.data?.detail || 'Fix failed' };
                  }
                }}
                onApplyAllFixes={async () => {
                  try {
                    const res = await api.post(`/funnels/${id}/apply-fixes`);
                    const data = res.data;
                    // Refetch funnel to get updated state
                    await refetch();
                    if (data.funnel?.draft) {
                      setLocalDraft(data.funnel.draft);
                    }
                    if (data.funnel?.draft_version) {
                      setBaseVersion(data.funnel.draft_version);
                    }
                    return data;
                  } catch (err) {
                    return { applied_fixes: [], initial_score: 0, final_score: 0, improvement: 0, error: err?.response?.data?.detail || 'Batch fix failed' };
                  }
                }}
              />
            </div>
          )}
          {/* MA-186 + MA-209: Loading gate with timeout fallback */}
          {(() => {
            const needsTheme = !!funnel?.profit_path_id;
            const themeReady = ppCustomTheme && lpTokens;
            const isThemeReady = !needsTheme || themeReady || themeTimeout;  // MA-209: Add timeout fallback
            
            if (!isThemeReady) {
              return (
                <div className="flex items-center justify-center h-[600px] bg-slate-900/50">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                    <p className="text-slate-400">Loading theme...</p>
                  </div>
                </div>
              );
            }
            
            // MA-269: REMOVED MA-219 exclusivity logic
            // ALWAYS pass both appearance (canonical) and lpTokens (seed defaults)
            // Components use: appearance → lpTokens precedence (NO hardcoded fallbacks)
            
            console.log('[MA-269] Passing both appearance + lpTokens:', {
              appearance_present: !!appearance,
              lpTokens_present: !!lpTokens,
              appearance_iconBg: appearance?.iconBackgroundColor,
              lpTokens_accent: lpTokens?.accent,
            });
            
            return (
          <VisualFunnelBuilder
            nodes={nodes}
            entryNodeId={localDraft?.entry_node_id}
            appearance={appearance}
            theme={ppCustomTheme}
            lpTokens={lpTokens}
            header={(() => {
              // MA-262 PART B: Use localDraft.header for immediate UI updates (editing)
              // When user changes icon/title, localDraft is updated via onHeaderChange
              // UI must reflect localDraft immediately, not wait for save
              // On initial load: localDraft.header is null, so fallback to funnel.header
              const headerValue = localDraft?.header || funnel?.header;
              console.log('[MA-262 FIX] FunnelDetailsPage passing EDITABLE header:', {
                'localDraft?.header (EDITABLE STATE)': localDraft?.header,
                'funnel?.header (SAVED STATE)': funnel?.header,
                'resolved_header (WHAT UI SEES)': headerValue,
              });
              return headerValue;
            })()}
            onNodesChange={(newNodes, meta) => {
              console.log("[FunnelDetailsPage] onNodesChange called with", newNodes.length, "nodes");
              const bookingNode = newNodes.find(n => n.node_type === "booking");
              if (bookingNode) {
                console.log("[FunnelDetailsPage] Booking node heading:", bookingNode.content?.heading);
              }
              setLocalDraft(prev => prev ? { ...prev, nodes: newNodes } : null);
              // P4-VFB-UX1 item D: a mutation-managed echo is ALREADY persisted
              // by its own `/mutate`. Marking the draft dirty for it enabled the
              // Save button after every auto-save and invited a redundant
              // full-graph `PUT /draft`. A local-only change (the legacy
              // non-mutation path) still marks it dirty.
              if (meta?.saveMode !== "automatic") setHasUnsavedChanges(true);
            }}
            onEntryNodeChange={(newEntryId) => {
              setLocalDraft(prev => prev ? { ...prev, entry_node_id: newEntryId } : null);
              setHasUnsavedChanges(true);
            }}
            onAppearanceChange={(newAppearance) => {
              console.log('[MA-305-TRACE] FunnelDetailsPage - onAppearanceChange called:', {
                newAppearance,
                hasOverrides: !!newAppearance?.appearance_overrides,
                overrides: newAppearance?.appearance_overrides,
                headerBg: newAppearance?.headerBackgroundColor,
                responseBg: newAppearance?.responseAreaColor,
                threadBg: newAppearance?.threadColor,
              });
              setAppearance(newAppearance);
              setHasUnsavedChanges(true);
            }}
            onHeaderChange={(newHeader) => {
              // MA-228: Initialize full draft from funnel when null to prevent write path failure
              // CRITICAL: Must preserve full funnel structure for schema compatibility
              setLocalDraft(prev => {
                if (!prev) {
                  // Initialize with full funnel structure + new header
                  return {
                    ...funnel,
                    header: newHeader,
                  };
                }
                // Update existing draft
                return { ...prev, header: newHeader };
              });
              setHasUnsavedChanges(true);
            }}
            onPreviewFunnel={handleTestFunnel}
            onSave={handleSave}
            funnelId={id}
            baseVersion={baseVersion}
            onMutationSuccess={handleMutationSuccess}
            onAutoSaveStateChange={handleAutoSaveStateChange}
            edges={localDraft?.edges || []}
            onEdgesChange={(newEdges, meta) => {
              setLocalDraft(prev => prev ? { ...prev, edges: newEdges } : null);
              //: same rule as nodes -- edge mutations are automatic too.
              if (meta?.saveMode !== "automatic") setHasUnsavedChanges(true);
            }}
            // MA-UI-106: Preview header props
            isPublished={!!funnel.published_at}
            // MA-AI-REVIEW: Approval button for AI-generated funnels
            showApprovalButton={showApprovalButton}
            onMarkApproved={handleMarkAllApproved}
            // MA-UI-107: Review progress props (moved from top bar to Preview header)
            reviewStats={displayStats}
            onNavigateToStep={handleNavigateToStep}
          />
            );
          })()}
        </TabsContent>
        
        {/* Analytics Tab Content */}
        <TabsContent value="analytics" className="flex-1 overflow-y-auto m-0">
          <FunnelAnalyticsTab 
            funnelId={id} 
            funnelName={funnel?.name}
            onNavigateToStep={(stepId, stepLabel) => {
              // Switch to builder tab and select the step
              setActiveTab("builder");
              // The builder will handle selecting the step via selectedNodeId
              // We use a small delay to let the tab switch complete
              setTimeout(() => {
                // Find the node and select it through the builder
                const stepIndex = draft?.nodes?.findIndex(n => n.id === stepId);
                if (stepIndex !== -1) {
                  // Trigger node selection - this will be picked up by VisualFunnelBuilder
                  window.dispatchEvent(new CustomEvent('select-funnel-step', { 
                    detail: { stepId, stepLabel } 
                  }));
                }
              }, 100);
            }}
          />
        </TabsContent>
        
        {/* Embed Tab Content */}
        <TabsContent value="embed" className="flex-1 overflow-y-auto m-0">
          <FunnelEmbedTab 
            funnelId={id}
            funnelSlug={funnel?.slug}
            funnelName={funnel?.name}
            embedKey={funnel?.embed_key}
            onRotateKey={async () => {
              // Call API to rotate embed key
              const response = await api.post(`/funnels/${id}/rotate-embed-key`);
              if (response.data) {
                // Refetch funnel to get new embed_key
                await refetch();
              }
            }}
          />
        </TabsContent>
        
        {/* Settings Tab Content (MA-UI-059) */}
        <TabsContent value="settings" className="flex-1 overflow-hidden m-0">
          <FunnelSettingsTab 
            funnelId={id}
            settings={funnelSettings}
            onSettingsChange={(newSettings) => {
              setFunnelSettings(newSettings);
              setHasUnsavedChanges(true);
            }}
            hasUnsavedChanges={hasUnsavedChanges}
            funnel={funnel}
            onFunnelUpdate={async (updates) => {
              // MA-IMPL-117: Persist funnel-level fields (attribution, campaign)
              // immediately via PATCH — these are not draft content
              try {
                await api.patch(`/funnels/${id}`, updates);
                await refetch();
                toast({ title: "Attribution updated" });
              } catch (err) {
                const detail = err.response?.data?.detail;
                const msg = typeof detail === "string" ? detail : detail?.message || "Failed to update";
                toast({ title: "Error", description: msg, variant: "destructive" });
              }
            }}
          />
        </TabsContent>
        
        {/* FunnelAssist Tab Content (MA-AI-OPT-001) */}
        <TabsContent value="assist" className="flex-1 overflow-y-auto m-0 p-4">
          <div className="max-w-3xl mx-auto">
            <FunnelAssistPanel 
              profitPathId={profitPathId}
              profitPathName={ppContext?.pp?.name}
              funnelId={id}
              funnelName={funnel?.name}
            />
          </div>
        </TabsContent>
        
        {/* History Tab Content (MA-MUTATION-UI) */}
        {/* MA-UI-113: Fixed height to fit viewport with internal scrolling */}
        <TabsContent value="history" className="flex-1 overflow-hidden m-0 p-4">
          <div className="max-w-2xl mx-auto h-full">
            <MutationTimeline 
              funnelId={id}
              maxHeight="calc(100vh - 220px)"
            />
          </div>
        </TabsContent>
      </Tabs>
      
      {/* Publish Confirmation Dialog */}
      <AlertDialog open={publishDialogOpen} onOpenChange={setPublishDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Publish Funnel</AlertDialogTitle>
            <AlertDialogDescription>
              This will make your funnel live and accessible via the embed code.
              {hasUnsavedChanges && " Your unsaved changes will be saved first."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          {publishValidationUnavailable && (
            <div className="space-y-2 py-2">
              <div
                className="flex items-center gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5"
                data-testid="publish-validation-unavailable"
              >
                <XCircle className="h-4 w-4 shrink-0 text-red-400" />
                <span className="text-sm text-red-200">
                  Publish validation could not be completed. Publishing is blocked until it succeeds.
                </span>
              </div>
            </div>
          )}

          {isAIGenerated && publishValidation && !publishValidation.can_publish && (
            <div className="space-y-2 py-2">
              {publishValidation.issues.map((issue, idx) => (
                <div key={idx} className="flex items-center gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5">
                  <XCircle className="h-4 w-4 shrink-0 text-red-400" />
                  <span className="text-sm text-red-200">{issue.message}</span>
                </div>
              ))}
              {publishValidation.warnings?.map((warning, idx) => (
                <div key={`w-${idx}`} className="flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-2.5">
                  <AlertTriangle className="h-4 w-4 shrink-0 text-amber-400" />
                  <span className="text-sm text-amber-200">{warning.message}</span>
                </div>
              ))}
            </div>
          )}
          
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handlePublish}
              disabled={publishBlocked}
              data-testid="publish-dialog-action"
            >
              {publishValidationUnavailable
                ? "Validation Unavailable"
                : publishBlocked
                  ? "Review Required"
                  : "Publish"
              }
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Save As Template Modal */}
      <SaveAsTemplateModal
        open={saveAsTemplateOpen}
        onClose={() => setSaveAsTemplateOpen(false)}
        funnelId={id}
        funnelName={funnel?.name}
      />
    </div>
  );
}
